import urls from "global/constants/UrlConstants";
import { SignRequest } from "models/interfaces";
import { getCallParams, makeCall } from "utils/service";
import {
  LibraryCategory,
  LibraryDocument,
  LibraryTemplate,
  LibraryTemplateFilter,
} from "./templateLibrary.types";

/** The server fixes the gallery page size; kept here only to know when a page was the last. */
export const LIBRARY_PAGE_SIZE = 24;

/** One page (1-based) of published templates matching the filter. */
export const fetchLibraryTemplates = async (
  page: number,
  filter: LibraryTemplateFilter,
  signal?: AbortSignal,
): Promise<LibraryTemplate[]> => {
  const url = `${urls.TEMPLATE_LIBRARY}/fetch/page/${page}`;
  const callParams = await getCallParams("POST", filter, signal);
  return makeCall(url, callParams);
};

/** How many published templates match the same filter. */
export const fetchLibraryTemplateCount = async (
  filter: LibraryTemplateFilter,
  signal?: AbortSignal,
): Promise<number> => {
  const url = `${urls.TEMPLATE_LIBRARY}/fetch/count`;
  const callParams = await getCallParams("POST", filter, signal);
  return makeCall(url, callParams);
};

/** Categories that have published templates, with counts. */
export const fetchLibraryCategories = async (): Promise<LibraryCategory[]> => {
  const url = `${urls.TEMPLATE_LIBRARY}/categories`;
  const callParams = await getCallParams("GET");
  return makeCall(url, callParams);
};

/** One published template, with its long description. 404 when not published. */
export const fetchLibraryTemplate = async (
  id: string,
): Promise<LibraryTemplate> => {
  const url = `${urls.TEMPLATE_LIBRARY}/${encodeURIComponent(id)}`;
  const callParams = await getCallParams("GET");
  return makeCall(url, callParams);
};

/** A short-lived (15 min) URL of the template's preview image. */
export const fetchLibraryTemplatePreview = async (
  id: string,
): Promise<string> => {
  const url = `${urls.TEMPLATE_LIBRARY}/${encodeURIComponent(id)}/preview`;
  const callParams = await getCallParams("GET");
  const response = await makeCall(url, callParams);
  return response?.message;
};

/** Every document (PDF) of a published template, with page counts and short-lived URLs. */
export const fetchLibraryTemplateDocuments = async (
  id: string,
): Promise<LibraryDocument[]> => {
  const url = `${urls.TEMPLATE_LIBRARY}/${encodeURIComponent(id)}/documents`;
  const callParams = await getCallParams("GET");
  return makeCall(url, callParams);
};

/**
 * Copies a published library template into My Templates (root folder) and
 * returns the new template, in the same shape the template list uses.
 *
 * @param documents for a kit, the names (as `/documents` returns them) of the
 *   documents to take. Empty or omitted takes every document.
 */
export const importLibraryTemplate = async (
  id: string,
  documents: string[] = [],
): Promise<SignRequest> => {
  const url = `${urls.IMPORT_LIBRARY_TEMPLATE}/${encodeURIComponent(id)}`;
  const callParams = await getCallParams("POST", { parentId: 0, documents });
  return makeCall(url, callParams);
};

// ---------------------------------------------------------------------------
// Session state (UI only). The API does not say whether this account already
// imported a template, so what was imported during this visit is kept here:
// the detail page shows its "Imported" state, and My Templates marks the copy
// "NEW". Both reset on reload.
// ---------------------------------------------------------------------------

/** Library id → id of the copy made in My Templates. */
const importedThisSession = new Map<string, string>();

export const markImported = (libraryId: string, templateId: string | number) =>
  importedThisSession.set(libraryId, String(templateId));

export const importedTemplateIdFor = (libraryId: string) =>
  importedThisSession.get(libraryId) ?? null;

export const isRecentlyImported = (templateId?: string | number) =>
  templateId != null &&
  Array.from(importedThisSession.values()).includes(String(templateId));
